import { Citation } from './types';

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Turn bare workflow UIDs (e.g. "LMR-1933") in the briefing markdown into clickable links, using
 * the UID→path map the agent returns in `citations`. This lets the model reference the current
 * workflow and every precedent in plain text (no fabricated URLs) while the app still deep-links
 * each one — mirroring Ask Clara's behavior. Paths point at the annotation studio (with version).
 */
const linkifyUids = (content: string, citations?: Citation[]): string => {
  if (!content || !citations || citations.length === 0) return content;

  const map: Record<string, string> = {};
  citations.forEach((c) => {
    const m = c.label.match(/LMR-\d+/i);
    if (!m || !c.path) return;
    const uid = m[0].toUpperCase();
    if (!map[uid]) map[uid] = c.path;
  });

  const uids = Object.keys(map);
  if (uids.length === 0) return content;

  // Longest first so e.g. LMR-12 doesn't shadow LMR-123.
  const pattern = uids.sort((a, b) => b.length - a.length).map(escapeRe).join('|');
  const re = new RegExp(`\\b(${pattern})\\b`, 'gi');

  return content.replace(re, (match: string, _g: string, offset: number, full: string) => {
    const path = map[match.toUpperCase()];
    if (!path) return match;
    // Don't double-link a UID already inside a markdown link.
    return full[offset - 1] === '[' ? match : `[${match}](${path})`;
  });
};

export default linkifyUids;
